import { performIssueGetRequest } from '@atlassian/jira-issue-reliability-fetch/src/index.tsx';
import {
	getClient,
	type GiraQuery,
} from '@atlassian/jira-issue-reliability-fetch/src/issue-view-gira-client.tsx';

import {
	type ActivitySortOrderType,
	NEWEST_FIRST,
	OLDEST_FIRST,
} from '@atlassian/jira-issue-shared-types/src/common/types/activity-sort-order-type.tsx';
import type { IssueCreatedHistoryItem } from '../../../types.tsx';
import type { IssueHistoryResult } from '../types.tsx';
import type { GraphQlResponse } from './types.tsx';
import { transformGraphQlResponse, transformCreateInfoResponse } from './utils.tsx';

type IssueKey = string;

export const IssueHistoryQuery: GiraQuery<GraphQlResponse> = `
    query IssueHistoryQuery($issueKey: String!, $startAt: Long, $maxResults: Int, $orderBy: String) {
        viewIssue(issueKey: $issueKey) {
            history(orderBy: $orderBy, startAt: $startAt, maxResults: $maxResults) {
                isLast
                totalCount
                startIndex
                nodes {
                    fieldId
                    fieldType
                    fieldSchema {
                        type
                        customFieldType
                    }
                    timestamp
                    actor {
                        avatarUrls {
                            large
                        }
                        displayName
                        accountId
                    }
                    fieldDisplayName
                    isDueToRedaction
					isDueToRedactionRestore
                    from {
                        ... on GenericFieldValue {
                            displayValue
                            value
                        }
                        ... on AssigneeFieldValue {
                            displayValue
                            value
                            avatarUrl
                        }
                        ... on PriorityFieldValue {
                            displayValue
                            value
                            iconUrl
                        }
                        ... on StatusFieldValue {
                            displayValue
                            value
                            categoryId
                        }
                        ... on WorkLogFieldValue {
                            displayValue
                            value
                            worklog {
                                id
                                timeSpent
                            }
                        }
                        ... on RespondersFieldValue {
                            displayValue
                            value
                            responders {
                                ari
                                name
                                type
                                avatarUrl
                            }
                        }
                    }
                    to {
                        ... on GenericFieldValue {
                            displayValue
                            value
                        }
                        ... on AssigneeFieldValue {
                            displayValue
                            value
                            avatarUrl
                        }
                        ... on PriorityFieldValue {
                            displayValue
                            value
                            iconUrl
                        }
                        ... on StatusFieldValue {
                            displayValue
                            value
                            categoryId
                        }
                        ... on WorkLogFieldValue {
                            displayValue
                            value
                            worklog {
                                id
                                timeSpent
                            }
                        }
                        ... on RespondersFieldValue {
                            displayValue
                            value
                            responders {
                                ari
                                name
                                type
                                avatarUrl
                            }
                        }
                    }
                }
            }
        }
    }
`;

const getIssueCreatedUrl = (issueKey: IssueKey): string =>
	`/rest/api/3/issue/${issueKey}?fields=creator,created`;

export const fetchIssueCreatedInfo = (issueKey: IssueKey): Promise<IssueCreatedHistoryItem> =>
	performIssueGetRequest(
		getIssueCreatedUrl(issueKey),
		undefined,
		'issue-actions/issue-history/src/services/issue-history/history-item-generator',
	).then(transformCreateInfoResponse);

export const fetchIssueHistory = async (
	issueKey: IssueKey,
	startAt: number,
	sortMode: ActivitySortOrderType,
	pageSize: number,
): Promise<IssueHistoryResult> => {
	const client = getClient('issue-actions/issue-history/src/history-item-generator/index');
	// @ts-expect-error - Property 'data' does not exist on type 'unknown'. | Property 'errors' does not exist on type 'unknown'.
	const { data, errors } = await client.query({
		query: IssueHistoryQuery,
		variables: {
			issueKey,
			startAt,
			maxResults: pageSize,
			orderBy: sortMode === OLDEST_FIRST ? 'created' : '-created',
		},
	});

	if (errors) {
		throw new Error(
			`Errors returned from IssueHistoryQuery result: ${errors
				// @ts-expect-error - TS7006 - Parameter 'x' implicitly has an 'any' type.
				.map((x) => x.message)
				.join(', ')}`,
		);
	}

	if (!data) {
		throw new Error(
			'Unexpected empty data object received from IssueHistoryQuery result with no errors',
		);
	}

	return transformGraphQlResponse(data, pageSize);
};

export const getIssueHistory = async (
	issueKey: IssueKey,
	startAt: number,
	sortMode: ActivitySortOrderType,
	pageSize: number,
): Promise<IssueHistoryResult> => {
	if (startAt === 0 && sortMode === OLDEST_FIRST) {
		const [issueHistory, createInfo] = await Promise.all([
			fetchIssueHistory(issueKey, startAt, sortMode, pageSize),
			fetchIssueCreatedInfo(issueKey),
		]);

		return {
			...issueHistory,
			history: [createInfo, ...issueHistory.history],
		};
	}

	const issueHistory = await fetchIssueHistory(issueKey, startAt, sortMode, pageSize);

	if (sortMode === NEWEST_FIRST && issueHistory.isLastPage) {
		const createInfo = await fetchIssueCreatedInfo(issueKey);

		return {
			...issueHistory,
			history: [...issueHistory.history, createInfo],
		};
	}

	return issueHistory;
};
